import type { AttachmentView as Attachment } from '@hilo/contracts';
import { useEffect, useRef, useState } from 'react';
import { IconAlert, IconClock } from '../../ui/icons';
import styles from './AttachmentView.module.css';

/** El contenido siempre pasa por la API, que comprueba el acceso en cada descarga. Nunca hay URL externa. */
export const contentUrl = (id: string) => `/api/v1/attachments/${id}/content`;

export function formatBytes(bytes: number | null): string {
  if (bytes === null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/**
 * Audio y video se cargan enteros (≤ 25 MiB) al pulsar reproducir y se sirven desde un objeto local:
 * así funcionan en todos los navegadores sin peticiones parciales (Range), que la API no ofrece.
 */
function useBlobUrl(id: string, enabled: boolean): { url: string | null; failed: boolean } {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    fetch(contentUrl(id), { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.blob() : Promise.reject(new Error(String(response.status)))))
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [id, enabled]);
  return { url, failed };
}

function Player({ attachment }: { attachment: Attachment }) {
  const [requested, setRequested] = useState(false);
  const { url, failed } = useBlobUrl(attachment.id, requested);
  const media = useRef<HTMLMediaElement | null>(null);
  useEffect(() => {
    if (url) void media.current?.play().catch(() => undefined);
  }, [url]);
  const label = attachment.kind === 'AUDIO' ? 'audio' : 'video';

  if (failed) return <p className={styles.problem}><IconAlert /> No se pudo cargar el {label}.</p>;
  if (!url) {
    return (
      <button type="button" className={styles.playCard} onClick={() => setRequested(true)} disabled={requested}
        aria-label={`Reproducir ${label}${attachment.name ? ` ${attachment.name}` : ''}`}>
        <span className={styles.playIcon} aria-hidden="true">
          {requested ? <span className={styles.spinner} /> : <svg viewBox="0 0 24 24"><path d="M8 5.5v13l10.5-6.5z" /></svg>}
        </span>
        <span className={styles.fileText}>
          <span className={styles.fileName}>{attachment.kind === 'AUDIO' ? 'Nota de voz o audio' : 'Video'}</span>
          <span className={styles.fileMeta}>{requested ? 'Cargando…' : formatBytes(attachment.bytes)}</span>
        </span>
      </button>
    );
  }
  return attachment.kind === 'AUDIO'
    ? <audio ref={(element) => { media.current = element; }} className={styles.audio} controls src={url} />
    : <video ref={(element) => { media.current = element; }} className={styles.video} controls src={url} />;
}

function Lightbox({ attachment, onClose }: { attachment: Attachment; onClose: () => void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    dialog.current?.showModal();
  }, []);
  return (
    <dialog ref={dialog} className={styles.lightbox} aria-label="Imagen ampliada"
      onCancel={(event) => { event.preventDefault(); onClose(); }}
      onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <img src={contentUrl(attachment.id)} alt={attachment.name ?? 'Imagen'} />
      <div className={styles.lightboxBar}>
        <a className="btn btn-secondary" href={contentUrl(attachment.id)} download={attachment.name ?? true}>Descargar</a>
        <button type="button" className="btn btn-primary" autoFocus onClick={onClose}>Cerrar</button>
      </div>
    </dialog>
  );
}

/** Un adjunto dentro de una burbuja del hilo. */
export function AttachmentView({ attachment }: { attachment: Attachment }) {
  const [zoom, setZoom] = useState(false);

  if (attachment.state === 'PENDING') {
    return <p className={styles.pending}><IconClock /> Descargando el archivo del cliente…</p>;
  }
  if (attachment.state === 'FAILED') {
    return <p className={styles.problem}><IconAlert /> No se pudo obtener este archivo.</p>;
  }
  if (attachment.kind === 'IMAGE') {
    return (
      <>
        <button type="button" className={styles.imageButton} onClick={() => setZoom(true)} aria-label={`Ampliar imagen${attachment.name ? ` ${attachment.name}` : ''}`}>
          <img className={styles.image} src={contentUrl(attachment.id)} alt="" loading="lazy" />
        </button>
        {zoom && <Lightbox attachment={attachment} onClose={() => setZoom(false)} />}
      </>
    );
  }
  if (attachment.kind === 'AUDIO' || attachment.kind === 'VIDEO') return <Player attachment={attachment} />;
  const extension = (attachment.name?.split('.').pop() ?? 'doc').slice(0, 4).toUpperCase();
  return (
    <a className={styles.fileCard} href={contentUrl(attachment.id)} download={attachment.name ?? true}>
      <span className={styles.fileBadge} aria-hidden="true">{extension}</span>
      <span className={styles.fileText}>
        <span className={styles.fileName}>{attachment.name ?? 'Documento'}</span>
        <span className={styles.fileMeta}>{formatBytes(attachment.bytes)} · Descargar</span>
      </span>
    </a>
  );
}
